import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { sortBy } from 'lodash/fp'
import React, { PropsWithChildren, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { OffsetLimitPaginationResponse } from '~/api/common'
import { frozenArr } from '~/common/arrayUtils2'
import { objectHookToContext } from '~/common/hookToContext'
import { Msg, useMsg } from '~/common/localizationUtils'
import {
  QueryOptsWithUserFn,
  useInternalListDataContext,
  useInternalListDataQueryResult
} from '~/components/v2/ResultsLayout/ListQueryProvider/ListQueryProvider'
import {
  AlertBanner,
  AlertTitle,
  Box,
  Button,
  ButtonProps,
  Checkbox,
  CheckboxProps,
  Divider,
  Flex,
  Spinner,
  Text
} from '~/design_system'
import { useLatestCallbackRef } from '~/hooks/useLatestRef'
import { AccountMeResponse, useAccountMeQuery } from '~/api/accounts'
import { system } from '@workday/canvas-tokens-web'
import { $thm } from '~/common/themeSx'

export interface ItemWithId {
  id: string | number
  [key: string]: any
}

export type BulkStrategyQuery = QueryOptsWithUserFn<number[]>

export interface BulkActionBarProviderProps {
  queryResult?: UseQueryResult<OffsetLimitPaginationResponse<any>>
  bulkActionLimit?: number
}

export const [BulkActionBarProvider, useBulkActionBarContext] = objectHookToContext(function useBulkActionBar({
  queryResult: queryResultFromProp,
  bulkActionLimit = 500
}: BulkActionBarProviderProps) {
  const { queryResult, bulkSelectStrategyOpts, queryParams, isItemBulkActionable } = useInternalListDataContext({
    fallback: queryResultFromProp
  })
  const [selectedItems, setSelectedItems] = useState<ItemWithId[]>([])
  const [areAllItemsOnAllPagesSelected, setAreAllItemsOnAllPagesSelected] = useState(false)
  const { data: userAccount = {} as AccountMeResponse } = useAccountMeQuery()

  const currentPageItems = queryResult.data?.results ?? (frozenArr as unknown as ItemWithId[])
  const selectableOnPage = useMemo(() => {
    // if the dev passed in a function to determine bulk action eligibility then we use that
    //  since they are doing the check on the front end
    if (isItemBulkActionable) {
      return currentPageItems.filter(item => isItemBulkActionable(item))
    }

    // If the dev has passed in a bulkSelectStrategyOpts then they are running the check
    //  on the back end using a combination of a bulk_eligible_api and also returning the
    //  items on the current page with a is_bulk_eligible property
    if (bulkSelectStrategyOpts) {
      return currentPageItems.filter(item => item.is_bulk_eligible)
    }

    // Otherwise just return all items on the current page
    return currentPageItems
  }, [currentPageItems, isItemBulkActionable])

  const isItemSelectable = useCallback(
    (item: ItemWithId) => {
      if (isItemBulkActionable) {
        return isItemBulkActionable(item)
      }
      if (bulkSelectStrategyOpts) {
        return item.is_bulk_eligible
      }
      return true
    },
    [isItemBulkActionable, bulkSelectStrategyOpts]
  )

  const isOnlyOnePageOfResults = queryResult?.data?.total_pages === 1
  const areAllItemsOnCurrentPageSelected = useMemo(() => {
    return (
      selectableOnPage.length > 0 &&
      selectedItems.length > 0 &&
      selectableOnPage.every(item => selectedItems.some(selectedItem => selectedItem.id === item.id))
    )
  }, [selectableOnPage, selectedItems])

  // only make the call to retrieve the bulk actionable ids if we are told to and
  //   (for performance reasons) only if there are multiple pages and if the user
  //   has selected all items on the current page
  const selectedIdOpts =
    bulkSelectStrategyOpts && areAllItemsOnCurrentPageSelected && !isOnlyOnePageOfResults
      ? bulkSelectStrategyOpts(queryParams, userAccount)
      : { enabled: false }
  const selectableIdsResult = useQuery(selectedIdOpts)

  let maxNumberOfSelectableItems = 0
  // if selectableIdsResult is present then we know what id's are selectable
  if (selectableIdsResult?.data) {
    maxNumberOfSelectableItems = selectableIdsResult?.data?.length
  } else if (isOnlyOnePageOfResults) {
    // if we only have one page of results then we already know which are selectable (overall)
    maxNumberOfSelectableItems = selectableOnPage.length
  } else if (queryResult.data?.count) {
    // if we're not using a bulk strategy then we can use the count from the query result
    maxNumberOfSelectableItems = queryResult.data.count
  }
  const selectedItemsCount = areAllItemsOnAllPagesSelected ? maxNumberOfSelectableItems : selectedItems.length
  const areActionsDisabled = selectedItemsCount === 0 || selectedItemsCount >= bulkActionLimit

  const areItemsSelected = useCallback(
    (items: ItemWithId[]) =>
      areAllItemsOnAllPagesSelected ||
      items.every(item => selectedItems.some(selectedItem => selectedItem.id === item.id)),
    [areAllItemsOnAllPagesSelected, selectedItems]
  )

  const isItemSelected = useCallback((currentItem: ItemWithId) => areItemsSelected([currentItem]), [areItemsSelected])

  const hasSelectedItemsOnCurrentPage = useLatestCallbackRef(
    (items: ItemWithId[]) =>
      items && selectedItems.some(selectedItem => items.some(item => item.id === selectedItem.id))
  )

  const selectItems = useLatestCallbackRef((currentItems: ItemWithId[]) => {
    const newSelectedItems = sortById([...selectedItems, ...currentItems])
    setSelectedItems(newSelectedItems)
  })

  const deselectItems = useLatestCallbackRef((currentItems: ItemWithId[]) => {
    const idsToRemove = currentItems.map(ci => ci.id)
    setSelectedItems(selectedItems.filter(item => !idsToRemove.includes(item.id)))
    setAreAllItemsOnAllPagesSelected(false)
  })

  const toggleItem = useLatestCallbackRef((item: ItemWithId) => {
    const isSelected = isItemSelected(item)

    if (isSelected) {
      deselectItems([item])
    } else {
      selectItems([item])
    }
  })

  const toggleAllItemsOnCurrentPage = useLatestCallbackRef(() => {
    let newSelectedItems: ItemWithId[] = []
    if (areAllItemsOnCurrentPageSelected) {
      newSelectedItems = selectedItems.filter(
        selectedItem => !currentPageItems.some(item => item.id === selectedItem.id)
      )
    } else {
      newSelectedItems = sortById([
        ...selectedItems,
        ...selectableOnPage.filter(item => !selectedItems.some(selectedItem => selectedItem.id === item.id))
      ])
    }
    setSelectedItems(newSelectedItems)
    if (newSelectedItems.length < maxNumberOfSelectableItems) {
      setAreAllItemsOnAllPagesSelected(false)
    }
  })

  const toggleAllItemsOnAllPages = useLatestCallbackRef(() => {
    // if the items that are selectable are controlled by an api then we
    //   use all of those as selected
    const newItems = selectableIdsResult?.data ? selectableIdsResult?.data.map(id => ({ id })) : selectableOnPage
    setSelectedItems(
      sortById([
        ...selectedItems,
        ...newItems.filter(item => !selectedItems.some(selectedItem => selectedItem.id === item.id))
      ])
    )
    setAreAllItemsOnAllPagesSelected(!areAllItemsOnAllPagesSelected)
  })

  const deselectAllItems = useLatestCallbackRef(() => {
    setSelectedItems([])
    setAreAllItemsOnAllPagesSelected(false)
  })

  const refreshSelectedItems = useEffectEvent((selectableOnPage: Array<ItemWithId>) => {
    if (areAllItemsOnAllPagesSelected) {
      setSelectedItems(
        sortById([
          // if selectabledIdResult is present then there may be { id: n } formatted
          //   objects in selectedItems and we'll want to replace those with the real thing
          ...selectableOnPage,
          ...selectedItems.filter(item => !selectableOnPage.some(selectedItem => selectedItem.id === item.id))
        ])
      )
    }
  })

  useEffect(() => {
    refreshSelectedItems(selectableOnPage)
  }, [selectableOnPage])

  const api = {
    selectedItems,
    selectedItemsCount,
    maxNumberOfSelectableItems,
    areAllItemsOnCurrentPageSelected,
    areAllItemsOnAllPagesSelected,
    isItemSelected,
    hasSelectedItemsOnCurrentPage,
    toggleItem,
    toggleAllItemsOnCurrentPage,
    toggleAllItemsOnAllPages,
    deselectAllItems,
    selectItems,
    deselectItems,
    bulkActionLimit: bulkActionLimit ?? 500,
    areActionsDisabled,
    selectableOnPage,
    isItemSelectable,
    isFetching: selectableIdsResult.isFetching
  }

  return useMemo(
    () => api,
    [
      api.selectedItems,
      api.areAllItemsOnCurrentPageSelected,
      api.areAllItemsOnAllPagesSelected,
      api.bulkActionLimit,
      api.areActionsDisabled,
      api.selectableOnPage,
      api.isItemSelectable,
      api.isFetching
    ]
  )
})

const sortById = sortBy<ItemWithId>([(o: ItemWithId) => Number(o.id)])

export interface BulkActionBarProps {
  queryResult?: UseQueryResult<OffsetLimitPaginationResponse<any>>
  bulkLimitSuggestedActionMsg?: React.ReactNode
  children?: React.ReactNode
}

/**
 * The BulkActionBar component
 */
function InnerBulkActionBar({
  queryResult: queryResultFromProp,
  bulkLimitSuggestedActionMsg,
  children
}: PropsWithChildren<BulkActionBarProps>) {
  const api = useBulkActionBarContext()
  const queryResult = useInternalListDataQueryResult({ fallback: queryResultFromProp })

  const deselectAllBtnRef = useRef<HTMLButtonElement>(null)
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)

  const isNoData = !queryResult?.data || queryResult?.data.count === 0 || api.selectableOnPage.length === 0
  const isActionableIdsLoading = api.isFetching
  const showSelectAllButton =
    api.areAllItemsOnCurrentPageSelected && !api.areAllItemsOnAllPagesSelected && !isActionableIdsLoading
  const showDeselectAllBtn = api.selectedItemsCount > 0 && !isActionableIdsLoading
  const isSelected = api.selectedItemsCount > 0

  return (
    <Box
      data-bulk-action-bar
      position="relative"
      sx={$thm({
        border: { $base: '1px', $canvas: 'none' },
        borderColor: { $base: `var(${system.color.border.strong})` },
        boxShadow: { $base: 'base', $canvas: 'none' }
      })}
      role="group"
      aria-label="Bulk Actions">
      <Flex
        data-testid="bulk-action-bar"
        p={4}
        justify="space-between"
        wrap="wrap"
        rowGap={4}
        columnGap={6}
        flexDirection={{ base: 'column', sm: 'row' }}
        borderRadius="inherit"
        sx={$thm({
          // Same inset as list row content, so "Select all" lines up with the row checkboxes.
          px: { $canvas: 6 },
          bg: {
            $base: isSelected ? 'system.color.bg.primary.soft' : 'gray.100',
            // Idle is transparent so the shared list-card (or grid bulk card) shows through.
            $canvas: isSelected ? 'system.color.bg.primary.soft' : 'transparent'
          }
        })}>
        <Flex align="center" gap={3} flexWrap="wrap">
          <Checkbox
            isChecked={api.areAllItemsOnCurrentPageSelected}
            onChange={api.toggleAllItemsOnCurrentPage}
            data-testid="bulk-action-checkbox"
            isDisabled={isNoData}
            ref={selectAllCheckboxRef}>
            <Msg id="common.ui.select_all_on_page" description="Select all on page" />
          </Checkbox>
          {api.selectedItemsCount > 0 && (
            <Text textStyle="bodySemi" data-testid="selected-item-count" aria-live="polite" aria-atomic="true">
              <Msg
                id="common.ui.number_items_selected"
                numItemsSelected={api.selectedItemsCount}
                description="# Selected"
              />
            </Text>
          )}
          {(showSelectAllButton || showDeselectAllBtn) && (
            <Divider
              orientation="vertical"
              variant="dark"
              h={6}
              flexGrow={{ base: 1, sm: 0 }}
              opacity={{ base: 0, sm: 0.6 }}
            />
          )}
          <Flex flexWrap="wrap" gap={4}>
            {showSelectAllButton && (
              <Button
                variant="link"
                data-testid="bulk-action-select-all-btn"
                onClick={() => {
                  api.toggleAllItemsOnAllPages()
                  setTimeout(() => deselectAllBtnRef.current?.focus(), 0)
                }}
                p={0}
                height="auto"
                _hover={{ bg: 'transparent', textDecoration: 'underline' }}>
                <Msg
                  id="common.ui.select_all_number_items_selected"
                  numItemsSelected={api.maxNumberOfSelectableItems}
                  description="Select All #"
                />
              </Button>
            )}
            {showSelectAllButton && showDeselectAllBtn && (
              <Divider
                orientation="vertical"
                variant="dark"
                h={6}
                flexGrow={{ base: 1, sm: 0 }}
                opacity={{ base: 0, sm: 0.6 }}
              />
            )}
            {showDeselectAllBtn && (
              <Button
                variant="link"
                data-testid="bulk-action-deselect-btn"
                onClick={() => {
                  api.deselectAllItems()
                  setTimeout(() => selectAllCheckboxRef.current?.focus(), 0)
                }}
                p={0}
                height="auto"
                _hover={{ bg: 'transparent', textDecoration: 'underline' }}
                ref={deselectAllBtnRef}>
                <Msg id="common.ui.deselect_all" description="Deselect All" />
              </Button>
            )}
          </Flex>
          {isActionableIdsLoading && <Spinner />}
        </Flex>
        <Flex
          gap={2}
          wrap="wrap"
          flexDirection={{ base: 'column', sm: 'row' }}
          flexGrow={{ base: 1, sm: 0 }}
          display={{ base: api.areActionsDisabled ? 'none' : 'flex', sm: 'flex' }}
          data-testid="bulk-action-actions">
          {children}
        </Flex>
      </Flex>

      {api.selectedItemsCount > api.bulkActionLimit && (
        <AlertBanner status="warning" borderRadius={0} boxShadow="base" p={4}>
          <AlertTitle aria-live="polite" aria-atomic="true">
            <Msg id="common.ui.can_only_perform_bulk_actions_below_limit" limit={api.bulkActionLimit} />{' '}
            {bulkLimitSuggestedActionMsg ?? <Msg id="common.ui.bulk_action_limit_suggested_action" />}
          </AlertTitle>
        </AlertBanner>
      )}
    </Box>
  )
}

export const BulkActionBar = Object.assign(InnerBulkActionBar, {
  ActionButton: BulkActionBarActionButton,
  Checkbox: BulkActionBarCheckbox
})

function BulkActionBarActionButton(props: ButtonProps) {
  const bulkActionBar = useBulkActionBarContext()
  return (
    <Button size="sm" isDisabled={bulkActionBar.areActionsDisabled} {...props}>
      {props.children}
    </Button>
  )
}

BulkActionBarActionButton.displayName = 'BulkActionBar.ActionButton'

function BulkActionBarCheckbox({
  item,
  isSelectable,
  itemDescription,
  ...props
}: { item: ItemWithId; isSelectable?: boolean; itemDescription?: string } & Omit<CheckboxProps, 'isChecked'>) {
  const bulkActionBar = useBulkActionBarContext()
  const msg = useMsg()

  if (bulkActionBar.selectableOnPage?.length === 0) {
    return null
  }

  // eslint-disable-next-line no-param-reassign
  isSelectable ??= bulkActionBar.isItemSelectable(item)
  const isChecked = bulkActionBar.isItemSelected(item)
  const label = itemDescription
    ? msg(isChecked ? 'common.ui.remove_from_bulk_selection' : 'common.ui.add_to_bulk_selection', { itemDescription })
    : msg('common.ui.add_to_selection')

  return (
    <Checkbox
      aria-label={label}
      {...props}
      isChecked={isChecked}
      onChange={() => {
        bulkActionBar.toggleItem(item)
      }}
      // "Hide" the checkbox if it's not selectable
      {...(!isSelectable && {
        isDisabled: true,
        opacity: 0,
        'aria-hidden': true,
        'pointer-events': 'none'
      })}
    />
  )
}

BulkActionBarCheckbox.displayName = 'BulkActionBar.Checkbox'
